/* eslint-disable sort-keys */
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import {
   RoleCallRecordingExtensionGroupInput,
   RoleCallRecordingExtensionInput,
   RoleInput,
   RolePrivilegeInput,
} from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { handleRoleReports } from '../../../common-components/helpers/role-and-user-reports-helper';
import { RoleDetailSaveModel, RolePrivilegeSaveModel } from '../models/role-detail-save.model';
import { deleteRoleMutation } from '../queries/delete-role.graphql';
import { DeleteRole, DeleteRoleVariables } from '../queries/generated/DeleteRole';
import { SaveRole, SaveRoleVariables } from '../queries/generated/SaveRole';
import { SaveRoleMutation } from '../queries/save-role.graphql';

@Injectable({ providedIn: 'root' })
export class RoleDetailApiService {
   constructor(private apiService: LcsApiService) {}

   save(
      roleDetail: RoleDetailSaveModel,
      roleFields: (keyof RoleInput)[],
      rolePrivilegeFields: string[],
      roleCallRecordingExtensionFields: (keyof RoleCallRecordingExtensionInput)[],
      roleCallRecordingExtensionGroupFields: (keyof RoleCallRecordingExtensionGroupInput)[]
   ): Observable<ExecutionResult<SaveRole>> {
      const role = this.createRoleInput(
         roleDetail,
         roleFields,
         roleCallRecordingExtensionFields,
         roleCallRecordingExtensionGroupFields
      );
      const rolePrivileges = this.createRolePrivilegesInput(roleDetail, rolePrivilegeFields);

      let deletedRoleReportIds: number[] = roleDetail.roleReports ? roleDetail.roleReports['removed'] : [];

      deletedRoleReportIds = deletedRoleReportIds || [];

      const hasDeletedRoleReports = deletedRoleReportIds && deletedRoleReportIds.length > 0;

      const deletedRolePrivilegeIds = this.getDeletedRolePrivilegeIds(roleDetail.rolePrivileges || []);

      const hasDeletedRolePrivileges = deletedRolePrivilegeIds && deletedRolePrivilegeIds.length > 0;

      const deletedRoleCallRecordingExtensionIds =
         roleDetail.roleCallExtensionRecordingPrivileges
            ?.filter((priv) => priv.isDeleted)
            .map((p) => p.roleCallRecordingExtensionId) ?? [];

      const hasDeletedCallRecordingExtensions = deletedRoleCallRecordingExtensionIds.length > 0;

      const deletedRoleCallRecordingExtensionGroupIds =
         roleDetail.roleCallExtensionGroupRecordingPrivileges
            ?.filter((priv) => priv.isDeleted)
            .map((p) => p.roleCallRecordingExtensionGroupId) ?? [];

      const hasDeletedCallRecordingExtensionGroups = deletedRoleCallRecordingExtensionGroupIds.length > 0;

      const variables: SaveRoleVariables = {
         role: role,
         rolePrivileges: rolePrivileges,
         deletedRoleReportIds,
         deletedRolePrivilegeIds,
         deletedRoleCallRecordingExtensionIds,
         deletedRoleCallRecordingExtensionGroupIds,
         hasDeletedRoleReports: hasDeletedRoleReports,
         hasDeletedRolePrivileges: hasDeletedRolePrivileges,
         hasDeletedCallRecordingExtensions,
         hasDeletedCallRecordingExtensionGroups,
      };

      return this.apiService.query<SaveRole, SaveRoleVariables>({
         query: SaveRoleMutation,
         variables: variables,
      });
   }

   deleteRole(roleId: number): Observable<ExecutionResult<DeleteRole>> {
      return this.apiService.query<DeleteRole, DeleteRoleVariables>({
         query: deleteRoleMutation,
         variables: {
            roleId: roleId,
         },
      });
   }

   private createRoleInput(
      role: RoleDetailSaveModel,
      roleFields: (keyof RoleInput)[],
      roleCallRecordingExtensionFields: (keyof RoleCallRecordingExtensionInput)[],
      roleCallRecordingExtensionGroupFields: (keyof RoleCallRecordingExtensionGroupInput)[]
   ): RoleInput {
      roleFields = [...roleFields, 'roleId'];

      const fields: string[] = roleFields;
      const roleInput: RoleInput = { fields, roleId: role.roleId, roleReports: null };

      roleFields.forEach((field) => {
         roleInput[field.toString()] = role[field];
      });

      handleRoleReports(role, roleInput, roleFields);

      if (roleCallRecordingExtensionFields.length > 0) {
         roleInput['roleCallRecordingExtensions'] = createRoleCallRecordingExtensionInput(role);
         formatRoleCallRecordingExtensionFields(
            roleInput.fields,
            'roleCallRecordingExtensions',
            roleCallRecordingExtensionFields
         );
      }

      if (roleCallRecordingExtensionGroupFields.length > 0) {
         roleInput['roleCallRecordingExtensionGroups'] = createRoleCallRecordingExtensionGroupInput(role);
         formatRoleCallRecordingExtensionGroupFields(
            roleInput.fields,
            'roleCallRecordingExtensionGroups',
            roleCallRecordingExtensionGroupFields
         );
      }

      return roleInput;
   }

   private createRolePrivilegesInput(role: RoleDetailSaveModel, rolePrivilegeFields: string[]) {
      const rolePrivileges: RolePrivilegeInput[] = [];
      const uniquePrivilegeIds: number[] = [];
      rolePrivilegeFields.forEach((field) => {
         let privilegeId: number = +field.split('.')[0];
         let rolePrivilegeField: string = field.split('.')[1];
         if (!uniquePrivilegeIds.includes(privilegeId)) {
            uniquePrivilegeIds.push(privilegeId);
            const rolePrivilege: RolePrivilegeInput = {
               fields: [
                  'isAdd',
                  'isView',
                  'isDelete',
                  'isEdit',
                  'isEnabled',
                  'roleId',
                  'privilegeId',
                  'rolePrivilegeId',
                  'accountId',
               ],
               roleId: role.roleId,
               privilegeId: privilegeId,
               accountId: role.accountId,
               rolePrivilegeId: role.rolePrivileges?.find((rolePrivilege) => rolePrivilege.privilegeId === privilegeId)
                  ?.rolePrivilegeId,
               isAdd:
                  (role.rolePrivileges?.find((rolePrivilege) => rolePrivilege.privilegeId === privilegeId) || {})[
                     'isAdd'
                  ] ?? false,
               isDelete:
                  (role.rolePrivileges?.find((rolePrivilege) => rolePrivilege.privilegeId === privilegeId) || {})[
                     'isDelete'
                  ] ?? false,
               isEdit:
                  (role.rolePrivileges?.find((rolePrivilege) => rolePrivilege.privilegeId === privilegeId) || {})[
                     'isEdit'
                  ] ?? false,
               isEnabled:
                  (role.rolePrivileges?.find((rolePrivilege) => rolePrivilege.privilegeId === privilegeId) || {})[
                     'isEnabled'
                  ] ?? false,
               isView:
                  (role.rolePrivileges?.find((rolePrivilege) => rolePrivilege.privilegeId === privilegeId) || {})[
                     'isView'
                  ] ?? false,
            };
            if (
               rolePrivilege.isAdd ||
               rolePrivilege.isView ||
               rolePrivilege.isEdit ||
               rolePrivilege.isDelete ||
               rolePrivilege.isEnabled
            ) {
               rolePrivileges.push(rolePrivilege);
            }
         }
      });

      return rolePrivileges;
   }

   private getDeletedRolePrivilegeIds(roles: RolePrivilegeSaveModel[]): number[] {
      return roles
         .filter(
            (role) =>
               !role.isAdd &&
               !role.isView &&
               !role.isEdit &&
               !role.isDelete &&
               !role.isEnabled &&
               role.rolePrivilegeId !== 0
         )
         .map((role) => role.rolePrivilegeId as number);
   }
}

function formatRoleCallRecordingExtensionFields(
   fields: string[],
   key: string,
   embedFields: (keyof RoleCallRecordingExtensionInput)[]
) {
   for (const embed of embedFields) {
      fields.push(`${key}.${embed}`);
   }
}

function formatRoleCallRecordingExtensionGroupFields(
   fields: string[],
   key: string,
   embedFields: (keyof RoleCallRecordingExtensionGroupInput)[]
) {
   for (const embed of embedFields) {
      fields.push(`${key}.${embed}`);
   }
}

function createRoleCallRecordingExtensionInput(role: RoleDetailSaveModel): RoleCallRecordingExtensionInput[] {
   let roleCallRecordingExtensionInput: RoleCallRecordingExtensionInput[];
   roleCallRecordingExtensionInput =
      role.roleCallExtensionRecordingPrivileges
         ?.filter((priv) => {
            return !priv.isDeleted;
         })
         .map((privilege) => {
            return {
               roleId: privilege.roleId,
               extensionId: privilege.extensionId,
               isInclude: privilege.isInclude,
               roleCallRecordingExtensionId: privilege.roleCallRecordingExtensionId,
               fields: [],
            };
         }) ?? [];
   return roleCallRecordingExtensionInput;
}

function createRoleCallRecordingExtensionGroupInput(role: RoleDetailSaveModel): RoleCallRecordingExtensionGroupInput[] {
   let roleCallRecordingExtensionGroupInput: RoleCallRecordingExtensionGroupInput[];
   roleCallRecordingExtensionGroupInput =
      role.roleCallExtensionGroupRecordingPrivileges
         ?.filter((priv) => {
            return !priv.isDeleted;
         })
         .map((privilege) => {
            return {
               roleId: privilege.roleId,
               extensionGroupId: privilege.extensionGroupId,
               isInclude: privilege.isInclude,
               roleCallRecordingExtensionGroupId: privilege.roleCallRecordingExtensionGroupId,
               fields: [],
            };
         }) ?? [];
   return roleCallRecordingExtensionGroupInput;
}
